import { SaveOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, message, Select, Spin, Switch } from "antd";
import { useEffect, useMemo } from "react";
import { parseApiError } from "@/shared/utils/error";
import { SmsNavigation } from "../components/SmsNavigation";
import { getSmsAutoSettings, getSmsTemplates, updateSmsAutoSettings } from "../services/sms.api";

const daysOptions = [
	{ label: "1 kun oldin/keyin", value: 1 },
	{ label: "2 kun oldin/keyin", value: 2 },
	{ label: "3 kun oldin/keyin", value: 3 },
	{ label: "5 kun oldin/keyin", value: 5 },
	{ label: "7 kun oldin/keyin", value: 7 },
	{ label: "10 kun oldin/keyin", value: 10 },
	{ label: "15 kun oldin/keyin", value: 15 },
];

export function SmsAutoSettingsPage() {
	const queryClient = useQueryClient();
	const [form] = Form.useForm();

	// 1. Fetch auto settings
	const { data: settings, isLoading: settingsLoading } = useQuery({
		queryKey: ["smsAutoSettings"],
		queryFn: getSmsAutoSettings,
	});

	// 2. Fetch templates for selection
	const { data: templatesData, isLoading: templatesLoading } = useQuery({
		queryKey: ["smsTemplates"],
		queryFn: () => getSmsTemplates({ limit: 100 }),
	});

	const templates = useMemo(() => templatesData?.items ?? [], [templatesData]);

	// Set form values once settings load
	useEffect(() => {
		if (settings) {
			form.setFieldsValue({
				isEnabled: settings.isEnabled,
				daysBefore: settings.daysBefore,
				daysAfter: settings.daysAfter,
				reminderTemplateId: settings.reminderTemplateId,
				overdueTemplateId: settings.overdueTemplateId,
			});
		}
	}, [settings, form]);

	// 3. Save settings mutation
	const saveMutation = useMutation({
		mutationFn: updateSmsAutoSettings,
		onSuccess: () => {
			message.success("Avtomatik SMS sozlamalari saqlandi");
			queryClient.invalidateQueries({ queryKey: ["smsAutoSettings"] });
		},
		onError: (err) => {
			message.error(parseApiError(err) || "Sozlamalarni saqlashda xatolik");
		},
	});

	const handleSave = () => {
		form
			.validateFields()
			.then((values) => {
				saveMutation.mutate({
					isEnabled: values.isEnabled,
					daysBefore: values.daysBefore || [],
					daysAfter: values.daysAfter || [],
					reminderTemplateId: values.reminderTemplateId || null,
					overdueTemplateId: values.overdueTemplateId || null,
				});
			})
			.catch(() => {
				message.error("Formani to'g'ri to'ldiring");
			});
	};

	if (settingsLoading || templatesLoading) {
		return (
			<div style={{ textAlign: "center", padding: "100px 0" }}>
				<Spin size="large" />
				<div style={{ marginTop: "16px", color: "var(--crm-ink-3)" }}>
					Avto-sozlamalar yuklanmoqda...
				</div>
			</div>
		);
	}

	return (
		<div className="crm-sms-auto-settings">
			{/* Page Head */}
			<div className="crm-page-head" style={{ marginBottom: "16px" }}>
				<div>
					<h1>SMS moduli</h1>
					<p>Eskiz integratsiyasi orqali tizim balansi va bildirishnomalarni boshqarish</p>
				</div>
			</div>

			{/* Sub-Navigation Tabs */}
			<SmsNavigation activeTab="auto" />

			<div className="crm-card" style={{ maxWidth: "600px" }}>
				<div className="crm-card-head">
					<h3>Avtomatik SMS bildirishnomalar sozlamalari</h3>
				</div>
				<div className="crm-card-body">
					<Form form={form} layout="vertical">
						<Form.Item
							label="Avtomatik SMS yuborish tizimi faol"
							name="isEnabled"
							valuePropName="checked"
							help="Ushbu rejim yoqilganda tizim har kuni ertalab soat 09:00 da qarzdorliklar bo'yicha avtomatik SMS yuboradi."
						>
							<Switch checkedChildren="Yoqilgan" unCheckedChildren="O'chirilgan" />
						</Form.Item>

						<Form.Item
							label="To'lov yaqinlashganda ogohlantirish kunlari (Days Before)"
							name="daysBefore"
							help="To'lov muddatiga qancha kun qolganda SMS yuborilishi (masalan: 1 kun, 3 kun oldin)"
						>
							<Select mode="multiple" options={daysOptions} placeholder="Kunlarni tanlang" />
						</Form.Item>

						<Form.Item
							label="To'lov eslatmasi shabloni"
							name="reminderTemplateId"
							help="Kunlik eslatma SMSlari uchun foydalaniladigan shablon."
						>
							<Select
								allowClear
								placeholder="Shablonni tanlang"
								options={templates.map((t) => ({ label: t.name, value: t.id }))}
							/>
						</Form.Item>

						<Form.Item
							label="To'lov kechikkanida ogohlantirish kunlari (Days After)"
							name="daysAfter"
							help="To'lov muddati o'tib ketgandan keyin qancha kun o'tgach SMS yuborilishi"
						>
							<Select mode="multiple" options={daysOptions} placeholder="Kunlarni tanlang" />
						</Form.Item>

						<Form.Item
							label="Kechikkan to'lov shabloni"
							name="overdueTemplateId"
							help="Muddati o'tgan to'lovlar uchun foydalaniladigan shablon."
						>
							<Select
								allowClear
								placeholder="Shablonni tanlang"
								options={templates.map((t) => ({ label: t.name, value: t.id }))}
							/>
						</Form.Item>
					</Form>

					<div
						style={{
							borderTop: "1px solid var(--crm-border)",
							paddingTop: "16px",
							marginTop: "8px",
						}}
					>
						<Button
							className="crm-primary-btn"
							icon={<SaveOutlined />}
							type="primary"
							loading={saveMutation.isPending}
							onClick={handleSave}
							style={{ minWidth: "200px" }}
						>
							Sozlamalarni saqlash
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default SmsAutoSettingsPage;
